// KeyFlowAiSurfaces -- the one launcher and the surfaces it opens (Phase 70, D-01, D-03).
//
// Mounted by App.js inside KeyFlowAiProvider. It owns the Help FAB, the Help
// drawer, and the desktop dock, so the routing between them lives in one place:
//
//   mode 'help'                         -> the Help drawer, exactly as before
//   mode 'ai', entitled, 1024px and up  -> the docked panel (non-modal)
//   mode 'ai' otherwise                 -> the Help drawer with the chat on top
//                                          (locked sheet or help-only inside the
//                                          chat for locked and unknown tiers)
//
// Eager file: it imports the Help drawer, the FAB, the context, and the access
// hook. The dock and the chat are React.lazy so they stay out of the eager bundle.
import React from 'react';
import HelpFab from '../../ui/HelpFab';
import HelpMenuPanel, { HelpSections } from '../../ui/HelpMenuPanel';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { useKeyFlowAiAccess } from '../../../hooks/useKeyFlowAiAccess';
import { useKeyFlowAi, DOCK_WIDTH } from './KeyFlowAiContext';

const KeyFlowAiDock = React.lazy(() => import('./KeyFlowAiDock'));
const KeyFlowAiChat = React.lazy(() => import('./KeyFlowAiChat'));

export const DESKTOP_QUERY = '(min-width: 1024px)';

// Which surface a launcher press lands on. Pure, so the routing is testable.
export function resolveLauncherSurface({ mode, ready, desktop }) {
    if (mode !== 'ai') return 'help';
    return ready && desktop ? 'dock' : 'drawer';
}

function pathOf(target) {
    // The proposal card's Open button passes a path string; citations pass { pathname, search }.
    if (typeof target === 'string') return target;
    return `${target.pathname || ''}${target.search || ''}`;
}

export default function KeyFlowAiSurfaces({
    user,
    workspaceId,
    workspace = null,
    hasUnseen = false,
    restartOnboarding,
    hasWorkspace = true,
    helpFocusWhatsNew = false,
    onFocusWhatsNewHandled,
    onSeeAllUpdates,
    whatsNew,
    openHelpRef = null,
    navigateTo,
    onDashboard = false,
    dealWriters = null,
    deps = {},
}) {
    const useAccess = deps.useKeyFlowAiAccess || useKeyFlowAiAccess;
    const access = useAccess(workspaceId);
    const ai = useKeyFlowAi();
    const desktop = useMediaQuery(DESKTOP_QUERY);
    const [helpOpen, setHelpOpen] = React.useState(false);

    // Phase 72: the setup card writes through App's own deal handlers. Kept in a ref on
    // the context so every Approve reads the current ones.
    React.useEffect(() => {
        if (!ai.dealWritersRef) return undefined;
        ai.dealWritersRef.current = dealWriters;
        return () => { ai.dealWritersRef.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the ref is stable; the writers change each render
    }, [dealWriters]);

    const mode = access.mode === 'ai' ? 'ai' : 'help';
    const surface = resolveLauncherSurface({ mode, ready: Boolean(access.ready), desktop });
    const dockOpen = surface === 'dock' && ai.open;
    // The build-version link must not stack a second chat on the open dock: route it
    // to the dock's Help view instead of opening the drawer.
    const routeRef = React.useRef({ dockOpen, setView: ai.setView });
    routeRef.current = { dockOpen, setView: ai.setView };

    // The sidebar build-version link opens Help straight onto What's New.
    React.useEffect(() => {
        if (!openHelpRef) return undefined;
        openHelpRef.current = () => {
            if (routeRef.current.dockOpen) routeRef.current.setView('help');
            else setHelpOpen(true);
        };
        return () => { openHelpRef.current = null; };
    }, [openHelpRef]);

    // A scoped "Ask KeyFlow AI" control (or the FAB) calls openKeyFlowAi(). When
    // the dock cannot host it (phone, tablet, locked, unknown tier), route the
    // request into the drawer and clear the context flag so the dock never
    // mounts on resize. The pin and session stay in the context.
    React.useEffect(() => {
        if (!ai.open) return;
        if (surface === 'dock') return;
        if (mode === 'ai') {
            ai.setView('chat');
            setHelpOpen(true);
        }
        ai.closeKeyFlowAi();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the open flag and the resolved surface
    }, [ai.open, surface, mode]);

    // The build-version link opens Help on What's New; in AI mode that means the
    // chat's help view, not the conversation.
    React.useEffect(() => {
        if (helpOpen && helpFocusWhatsNew && mode === 'ai') ai.setView('help');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the open request only
    }, [helpOpen, helpFocusWhatsNew, mode]);

    // The page sits beside the dock, never under it (D-03).
    React.useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--ai-dock-width', dockOpen ? `${DOCK_WIDTH}px` : '0px');
        return () => root.style.setProperty('--ai-dock-width', '0px');
    }, [dockOpen]);

    const rememberOpener = () => {
        const active = document.activeElement;
        ai.openerRef.current = active && active !== document.body
            ? active
            : document.querySelector('[data-tour="help-button"]');
    };

    const handleOpenPanel = () => {
        if (mode !== 'ai') { setHelpOpen(true); return; }
        rememberOpener();
        ai.openKeyFlowAi();
    };

    const handleClosePanel = () => {
        setHelpOpen(false);
        ai.closeKeyFlowAi();
    };

    const closeDrawer = () => setHelpOpen(false);

    // Desktop: the dock stays open and only the page changes. Phone: the drawer
    // closes, the page changes, and the conversation is intact on reopen.
    const handleCitation = (target) => {
        if (!target) return;
        if (surface !== 'dock') closeDrawer();
        if (typeof navigateTo === 'function') navigateTo(pathOf(target));
    };

    const renderHelpFor = (onClose) => function renderDockHelp() {
        return (
        <HelpSections
            onClose={onClose}
            restartOnboarding={restartOnboarding}
            hasWorkspace={hasWorkspace}
            whatsNew={whatsNew}
            onSeeAllUpdates={onSeeAllUpdates}
        />
        );
    };

    const chatProps = { workspaceId, uid: user?.uid || null, workspace, onDashboard };

    const buildDrawerSlot = ({ renderHelp }) => (
        <React.Suspense fallback={null}>
            <KeyFlowAiChat
                {...chatProps}
                compact
                onClose={closeDrawer}
                renderHelp={renderHelp}
                onNavigate={handleCitation}
                deps={deps.chat}
            />
        </React.Suspense>
    );
    const drawerSlot = mode === 'ai' ? buildDrawerSlot : null;

    return (
        <>
            <HelpFab
                hasUnseen={hasUnseen}
                mode={mode}
                panelOpen={helpOpen || dockOpen}
                rightOffset={dockOpen ? DOCK_WIDTH : 0}
                onOpenPanel={handleOpenPanel}
                onClosePanel={handleClosePanel}
            />
            <HelpMenuPanel
                open={helpOpen}
                onClose={closeDrawer}
                user={user}
                restartOnboarding={restartOnboarding}
                hasWorkspace={hasWorkspace}
                focusWhatsNew={helpFocusWhatsNew}
                onFocusWhatsNewHandled={onFocusWhatsNewHandled}
                onSeeAllUpdates={onSeeAllUpdates}
                whatsNew={whatsNew}
                keyflowAiSlot={drawerSlot}
            />
            {dockOpen && (
                <React.Suspense fallback={null}>
                    <KeyFlowAiDock
                        {...chatProps}
                        onNavigate={handleCitation}
                        renderHelp={renderHelpFor(ai.closeKeyFlowAi)}
                        deps={deps.chat}
                    />
                </React.Suspense>
            )}
        </>
    );
}
